// src/data/content.js

import React from 'react';
import { 
    AlertCircle, Users, Utensils, Mountain, 
    Star, Megaphone, Gift, Scroll, Palette, PartyPopper,
    Activity, Moon, Brain, FileText, Heart, ClipboardList,
    
} from 'lucide-react';

export const leadershipData = {
    intro: {
        title: "The Performance Imperative",
        content: "Workplaces can either strain or strengthen mental wellness. Meaningful work, supportive leadership, and healthy organizational cultures are associated with improved well-being, engagement, and productivity. Leaders who are attentive to mental wellness help create conditions where people choose to stay engaged, develop their skills, and grow."
    },
    protocols: {
        depression: {
            title: "Depression in the Workplace",
            description: "Depression affects how a person feels, thinks, and functions. It goes beyond temporary sadness.",
            actions: [
                { step: "Observe", text: "Notice changes in behavior, mood, or performance." },
                { step: "Address", text: "Speak privately and without distractions. Use observations rather than judgments." },
                { step: "Affirm", text: "Emphasize care and concern, not performance evaluation." },
                { step: "Understand", text: "Ask open-ended questions and listen without trying to fix." },
                { step: "Support", text: "Discuss accommodations, resources, or next steps collaboratively." },
                { step: "Follow Up", text: "Check in regularly and document interactions appropriately." }
            ]
        },
        anxiety: {
            title: "Anxiety & Uncertainty",
            description: "Anxiety often involves persistent worry related to future uncertainty. Common triggers include high-stakes evaluations or unclear expectations.",
            actions: [
                { step: "Create Structure", text: "Clear routines, expectations, and plans reduce uncertainty." },
                { step: "Reframe Anxiety", text: "Help individuals view physiological arousal as manageable and performance-enhancing." },
                { step: "Encourage Mindfulness", text: "Simple practices can improve focus and emotional regulation." },
                { step: "Clarify Stressors", text: "Identify specific concerns and develop practical plans." }
            ]
        },
        trauma: {
            title: "Trauma & The CARE Model",
            description: "Many individuals carry effects of past trauma. Leaders must balance empathy with clear boundaries.",
            actions: [
                { step: "Context", text: "Consider background factors and patterns (The 'C' in CARE)." },
                { step: "Attitude", text: "Lead with patience, empathy, and problem-solving (The 'A' in CARE)." },
                { step: "Response", text: "Stay calm, consistent, and emotionally regulated (The 'R' in CARE)." },
                { step: "Evaluate", text: "Reflect on boundaries, seek guidance, and document (The 'E' in CARE)." }
            ]
        }
    },
    signs: [
        "Sleep Disturbances", "Changes in Appetite", "Persistent Low Mood", 
        "Difficulty Coping", "Cognitive Difficulties", "Risky/Uncharacteristic Behavior"
    ],
    toolkit: [
        { 
            title: "Psychological Safety Assessment", 
            type: "PDF", 
            icon: <ClipboardList size={20}/>,
            content: "ASSESSMENT: TEAM PSYCHOLOGICAL SAFETY\n\nRate the following on a scale of 1-5:\n\n1. If you make a mistake on this team, it is often held against you.\n2. Members of this team are able to bring up problems and tough issues.\n3. People on this team sometimes reject others for being different.\n4. It is safe to take a risk on this team.\n5. It is difficult to ask other members of this team for help.\n\nSCORING:\nHigh Safety (20-25): Team feels secure to innovate.\nLow Safety (<15): Focus on trust-building exercises." 
        },
        { 
            title: "1-on-1 Meeting Guide", 
            type: "Template", 
            icon: <Users size={20}/>,
            content: "LEADERSHIP: 1-ON-1 MEETING STRUCTURE\n\n1. WELLBEING CHECK-IN (5 Mins)\n- 'How are you managing your energy levels this week?'\n- 'Any blockers outside of work I should be aware of?'\n\n2. PERFORMANCE & BLOCKERS (15 Mins)\n- 'What is the biggest hurdle in your current project?'\n- 'Where do you need my support?'\n\n3. GROWTH & FEEDBACK (10 Mins)\n- 'What skill are you trying to develop right now?'\n- Feedback from Leader: [Observation] + [Impact] + [Request]" 
        },
        { 
            title: "Return to Work Protocol", 
            type: "Guide", 
            icon: <FileText size={20}/>,
            content: "RETURN TO WORK PROTOCOL (Post-Leave)\n\nPHASE 1: PRE-RETURN (2 Weeks Prior)\n- Schedule 'No-Agenda' coffee chat.\n- Discuss accommodations (flexible hours, reduced load).\n\nPHASE 2: DAY 1 ORIENTATION\n- Focus on connection, not catch-up.\n- Review key changes that occurred during absence.\n\nPHASE 3: RAMP-UP (Weeks 1-4)\n- Week 1: 50% Workload\n- Week 2: 75% Workload\n- Regular check-ins on fatigue/stress levels." 
        },
        { 
            title: "Debriefing Critical Incidents", 
            type: "PDF", 
            icon: <AlertCircle size={20}/>,
            content: "CRITICAL INCIDENT DEBRIEF\n\nGoal: Process the event without re-traumatizing.\n\n1. FACTS: What happened? (Stick to objective timeline)\n2. THOUGHTS: What was your first thought when it occurred?\n3. REACTIONS: What was the hardest part for you?\n4. SYMPTOMS: How have you been sleeping/eating since?\n5. TEACHING: Normalizing reactions to abnormal events.\n6. RE-ENTRY: Plan for immediate next steps." 
        },
        { 
            title: "Manager's Self-Care Checklist", 
            type: "Worksheet", 
            icon: <Heart size={20}/>,
            content: "LEADER SELF-CARE AUDIT\n\n[ ] Am I modelling the boundaries I expect from my team?\n[ ] Have I taken a lunch break away from my desk this week?\n[ ] Who is my support system for difficult decisions?\n[ ] When was the last time I disconnected fully for 24 hours?\n\nReminder: You cannot pour from an empty cup. Your regulation sets the team's regulation." 
        }
    ]
};

export const familyHuddleContent = {
    intro: {
        quotes: [
            { text: "Family is not an important thing, it’s everything", author: "Michael J. Fox" }, 
            { text: "A happy family is but an earlier heaven", author: "George Bernard Shaw" } 
        ],
        history: "The huddle was invented by Paul Hubbard at Gallaudet University to shield sign language plays from opposing teams. It is the place that gets everyone on the same page.",
        purpose: "Human beings crave connection. While virtual connections (smartphones) captivate us, they are no substitute for real connection. Families offer the deepest source of psychological identity and security.",
        culture: "Every family has a unique culture. Successful teams, like the All Blacks, use principles like 'clean out the shed' (humility) to build culture. The same ingredients that build a sports team can build a strong family culture."
    },
    weeks: [
        {
            week: 1,
            title: "Create Connection",
            icon: <Utensils size={24} />,
            activity: "Have a meal together (Sunday or Monday). Order in or prepare a meal.", 
            task: "Start a conversation where each person answers specific questions. Parents close with a 'clincher' to build curiosity for next week.", 
            listTitle: "Conversation Starters",
            listIcon: "?",
            questions: [
                "What was the best part of your week?", 
                "What was the worst part of your week?", 
                "What is a memory that stands out?", 
                "What was something fun you did?", 
                "What was something hard/challenging?" 
            ]
        },
        {
            week: 2,
            title: "Experience Awesome",
            icon: <Mountain size={24} />,
            activity: "Go for a walk in nature (park, mountains, or sunset).", 
            task: "Immerse in 'Awe'—the emotion felt when encountering something vast. Discuss what you observed and how it made you feel.", 
            listTitle: "Reflections",
            listIcon: "?",
            questions: [
                "What did you observe during the walk?", 
                "What was surprising to you?", 
                "How did this make you feel?", 
                "What nuances (wind, clouds, critters) did you notice?" 
            ]
        },
        {
            week: 3,
            title: "Gratitude Practice",
            icon: <Star size={24} />,
            activity: "Tell a specific story of something you are grateful for.", 
            task: "Gratitude rewires the brain. Share a story using the 'Challenge-Action-Feeling' structure.", 
            listTitle: "Story Structure Guide",
            listIcon: "★",
            questions: [
                "1. The Challenge: Describe a difficult situation you or someone else faced.",
                "2. The Action: What specific action did someone take to help?",
                "3. The Feeling: How did that help make you feel valued or cared for?" 
            ]
        },
        {
            week: 4,
            title: "Show Up & Cheer",
            icon: <Megaphone size={24} />,
            activity: "Everyone shows up at one family member's event (sports, recital, play).", 
            task: "Be an active participant. Dress up, cheer loud, and minimize distractions (no phones). Children feel joy when they sense genuine excitement.", 
            listTitle: "Rules of Engagement",
            listIcon: "!",
            questions: [
                "Preparation: Dress up or wear colors to show support.",
                "Focus: Put phones away completely during the event.",
                "Support: Cheer loudly and be the 'active' audience.",
                "Follow-up: Ask them 'What was your favorite moment?' afterwards."
            ]
        },
        {
            week: 5,
            title: "Random Acts of Kindness",
            icon: <Gift size={24} />,
            activity: "Draw names from a hat. Perform a kindness using their 'Love Language'.", 
            task: "Identify the Love Language of the person you drew and act on it. Be genuine, not dutiful.", 
            listTitle: "The 5 Love Languages (Definitions)",
            listIcon: "♥",
            questions: [
                "Words of Affirmation: Speak love through compliments, encouragement, or a simple 'I appreciate you'.",
                "Acts of Service: Show love by doing helpful things—taking out the trash, making coffee, or easing their workload.",
                "Quality Time: Show love through undivided attention. Meaningful conversations or shared activities with zero distractions.",
                "Physical Touch: Show love through appropriate closeness—hugs, high-fives, holding hands, or sitting close.",
                "Receiving Gifts: Show love through thoughtful symbols. It's not about price, but saying 'I saw this and thought of you'."
            ]
        },
        {
            week: 6,
            title: "Shared Memory",
            icon: <Scroll size={24} />,
            activity: "Memorize a famous quote, passage, poem, or prayer together.", 
            task: "Practice repeating it throughout the week. Shared memorization creates a 'mental anchor' and family identity.", 
            listTitle: "Discussion & Practice",
            listIcon: "∞",
            questions: [
                "Why this passage?: Discuss why this specific text matters to your family.",
                "Repetition: Recite it once at every meal this week.",
                "Meaning: Ask 'How does this apply to our lives today?'"
            ]
        },
        {
            week: 7,
            title: "Family Creation",
            icon: <Palette size={24} />,
            activity: "Create something tangible together.", 
            task: "Rotate ideas and build something that everyone contributes to. The act of creating reflects family cohesion.", 
            listTitle: "Creation Ideas",
            listIcon: "💡",
            questions: [
                "Bake Together: Make homemade pizza or dessert where everyone has a specific job.",
                "Build Together: Work on a Lego set, model, or small woodworking project.",
                "Art Project: Paint a family mural, canvas, or create a comic strip about your family.",
                "Memory Book: Create a photo album or scrapbook with handwritten captions.",
                "Problem Solve: Complete a complex puzzle together."
            ]
        },
        {
            week: 8,
            title: "The Grand Finale",
            icon: <PartyPopper size={24} />,
            activity: "Formal Dinner & Movie Night.", 
            task: "Dress up formally. Recap the last 8 weeks during dinner. Finish with a toast/cheers.", 
            listTitle: "Dinner Discussion",
            listIcon: "?",
            questions: [
                "What was your favorite activity from the past 8 weeks?", 
                "What surprised you about doing these activities together?", 
                "What would you do differently next time?",
                "Toast: Finish the night by raising a glass and saying 'Cheers' to the family."
            ]
        }
    ]
};

export const initialTopicContent = {
    'kidzcorner':{
        title: "Kidz Corner",
        description: "Fun activities and relaxation tools.",
        modules: [
            {
                id: 'lib_core',
                title: "Core Library",
                resources: [
                    { id: 1, title: "SMART Goal Setting", tags: ["Self", "Family"], type: "PDF" },
                    { id: 2, title: "Visualization Audio", tags: ["Self"], type: "Audio" },
                    { id: 3, title: "Parent Code of Conduct", tags: ["Kids"], type: "PDF" },
                    { id: 4, title: "Feedback Script", tags: ["Family"], type: "PDF" },
                    { id: 5, title: "Weekly Training Log", tags: ["Self"], type: "Worksheet" },
                    { id: 6, title: "Meeting Planner", tags: ["Family"], type: "Template" },
                    { id: 7, title: "Digital Detox Contract", tags: ["Self", "Family"], type: "PDF" },
                ]
            }
        ]
    },
    
    // --- UPGRADED: TRANSITION TOPIC ---
    transition: {
        title: "Transition & Growth",
        description: "Navigating major life pivots: Moving to/from university sports, climbing the professional ladder, and redefining your identity beyond your current role.",
        modules: [
            {
                id: 'trans_identity',
                title: "1. Identity & The Pivot",
                resources: [
                    { id: 't1', title: "You Are More Than Your Title", type: "Video", url: "https://youtu.be/s8YjC3H_6O0", tags: ["Identity", "Mindset"] },
                    { id: 't2', title: "The Void: Managing the Loss of a Past Chapter", type: "Video", url: "https://youtu.be/imKRFa-K3wo", tags: ["Mental Health", "Change"] },
                    { id: 't3', title: "Identity Mapping Exercise", type: "Worksheet", content: "IDENTITY MAPPING\n\nWhen we transition, we often lose the labels that defined us (e.g., 'Student', 'Athlete', 'Junior Analyst').\n\n1. Who were you in your last chapter? (List 3 traits, not titles)\n2. What values remain constant regardless of your environment?\n3. Who do you want to become in this next chapter?\n\nRemember: Your value is intrinsic, not attached to your current uniform or job title.", tags: ["Identity", "Reflection"] }
                ]
            },
            {
                id: 'trans_campus',
                title: "2. The Campus Jump (Student-Athletes)",
                resources: [
                    { id: 't4', title: "Rookie to Veteran: Managing Expectations", type: "Video", url: "https://youtu.be/TMERth_0tvc", tags: ["University", "Athletics"] },
                    { id: 't5', title: "Life After the Final Whistle", type: "Video", url: "https://youtu.be/mM9xbjkZUJI", tags: ["Graduation", "Retirement"] },
                    { id: 't6', title: "Student-Athlete Time Matrix", type: "Template", content: "WEEKLY TIME BLOCKING\n\nUniversity transitions require ruthless prioritization. Use this template to claim your time.\n\n1. NON-NEGOTIABLES (Sleep 8h, Classes, Practice)\n2. HIGH-IMPACT FLEX TIME (Studying, Recovery/Treatment)\n3. SOCIAL & DOWNTIME (Essential for burnout prevention)\n4. TIME DRAINS (Scrolling, unstructured waiting)\n\nRule of thumb: If it's not on the calendar, it doesn't exist.", tags: ["Time Management", "Habits"] }
                ]
            },
            {
                id: 'trans_career',
                title: "3. The Corporate Climb",
                resources: [
                    { id: 't7', title: "Imposter Syndrome in New Roles", type: "Video", url: "https://youtu.be/j0NcFtaz4Dg", tags: ["Career", "Confidence"] },
                    { id: 't8', title: "From Player to Coach: Leadership Transitions", type: "Video", url: "https://youtu.be/VMcPEJaQ3Hs", tags: ["Leadership", "Career"] },
                    { id: 't9', title: "First 90 Days Protocol", type: "Template", content: "THE FIRST 90 DAYS: ENTERING A NEW ROLE\n\nDays 1-30: The Sponge\n- Goal: Listen and Learn. Do not try to change everything.\n- Action: Have 1-on-1s with key stakeholders. Ask: 'What is working? What is broken?'\n\nDays 31-60: The Contributor\n- Goal: Secure early, small wins.\n- Action: Identify low-hanging fruit and solve one visible problem.\n\nDays 61-90: The Leader\n- Goal: Shift from learning to guiding.\n- Action: Present your strategic vision or process improvements based on your initial observations.", tags: ["Career", "Planning"] }
                ]
            },
            {
                id: 'trans_planning',
                title: "4. Practical Future Planning",
                resources: [
                    { id: 't10', title: "Transferable Skills Matrix", type: "Worksheet", content: "TRANSFERABLE SKILLS AUDIT\n\nAthletes and high-performers possess skills that directly translate to the business world. Translate your past experience into corporate value:\n\nAthletic Trait -> Corporate Translation\n1. 'I watched game film' -> 'I conduct competitive analysis and performance reviews.'\n2. 'I played through adversity' -> 'I am resilient and maintain focus during crisis management.'\n3. 'I was team captain' -> 'I have proven experience in team leadership and conflict resolution.'\n4. 'I trained 6 days a week' -> 'I am highly disciplined, goal-oriented, and process-driven.'\n\nList 3 of your own translations below:", tags: ["Career", "Resume"] },
                    { id: 't11', title: "Basic Financial Runway Template", type: "Template", content: "FINANCIAL TRANSITION AUDIT\n\nTransitions often mean income changes. Know your numbers.\n\n1. CURRENT BURN RATE (Monthly)\n- Housing: $\n- Food: $\n- Transport: $\n- Debt/Loans: $\n- Non-negotiables: $\nTOTAL BURN: $\n\n2. RUNWAY CALCULATION\nTotal Savings ($) / Monthly Burn ($) = Months of Runway\n\n3. THE BRIDGE PLAN\nWhat part-time work, side-hustles, or network connections can bridge the gap while you transition to your new full-time goal?", tags: ["Finance", "Planning"] }
                ]
            }
        ]
    },

    // --- MENTAL HEALTH SECTIONS (10 Base Videos Each) ---
    anxiety: {
        title: "Anxiety",
        description: "Tools to down-regulate your nervous system and regain control.",
        modules: [
            { 
                id: 'core_101', 
                title: "Core Concepts (101 Series)", 
                resources: [
                    { id: 'v1', title: "1. Understanding Anxiety", type: "Video", url: "https://youtu.be/1CCg4ppDeX4" },
                    { id: 'v2', title: "2. What Anxiety Really Is", type: "Video", url: "https://youtu.be/N8wtuF9X6Nk" },
                    { id: 'v3', title: "3. How Anxiety Affects The Brain and Body", type: "Video", url: "https://youtu.be/ynLG7t39s7M" },
                    { id: 'v4', title: "4. Why Some People Are More Vulnerable", type: "Video", url: "https://youtu.be/k0TUTLiG-G0" },
                    { id: 'v5', title: "5. The Roots and Triggers of Anxiety", type: "Video", url: "https://youtu.be/mPPKZcDCJKs" },
                    { id: 'v6', title: "6. Responding to Anxiety with Intention", type: "Video", url: "https://youtu.be/gdoUE_fzARY" },
                    { id: 'v7', title: "7. The Power of Reframing Thoughts", type: "Video", url: "https://youtu.be/mnx6meHvtNQ" },
                    { id: 'v8', title: "8. Connection and Seeking Support", type: "Video", url: "https://youtu.be/jqT4Qy1Vbu0" },
                    { id: 'v9', title: "9. Building a Mindful Relationship with Anxiety", type: "Video", url: "https://youtu.be/qSF2kPw0ceA" },
                ] 
            }
        ]
    },
    depression: {
        title: "Depression",
        description: "Small, actionable steps to build momentum.",
        modules: [
            { 
                id: 'core_101', 
                title: "Core Concepts (101 Series)", 
                resources: [
                    { id: 'v1', title: "1. Understanding Depression", type: "Video", url: "https://youtu.be/Uqol72_S-MU" },
                    { id: 'v2', title: "2. What Depression Really Is", type: "Video", url: "https://youtu.be/hVnWHBgFIqk" },
                    { id: 'v3', title: "3. How Depression Affects the Brain", type: "Video", url: "https://youtu.be/3dmwTXYGvq8" },
                    { id: 'v4', title: "4. Why Some People Are More at Risk", type: "Video", url: "https://youtu.be/ie6lIjpCui4" },
                    { id: 'v5', title: "5. Causes of Depression", type: "Video", url: "https://youtu.be/GSWCpm2WKQw" },
                    { id: 'v6', title: "6. Taking Action Against Depression", type: "Video", url: "https://youtu.be/1hiigAIldlM" },
                    { id: 'v7', title: "7. Reframing Your Thoughts", type: "Video", url: "https://youtu.be/znreqsrZukM" },
                    { id: 'v8', title: "8. Reaching Out and Finding Help", type: "Video", url: "https://youtu.be/2y5lngjk0Uw" },
                    { id: 'v9', title: "9. Getting the Right Support", type: "Video", url: "https://youtu.be/NV0bb6O19ug" },
                    { id: 'v10', title: "10. Healthy Habits for Mental Wellness", type: "Video", url: "https://youtu.be/u97sj4jXz4U" },
                ] 
            }
        ] 
    },
    adhd: {
        title: "ADHD",
        description: "Executive function tools.",
        modules: [
            { 
                id: 'core_101', 
                title: "Core Concepts (101 Series)", 
                resources: [
                    { id: 'v1', title: "1. What ADHD Really Means", type: "Video", url: "https://youtu.be/jiPv9jqQB2c" },
                    { id: 'v2', title: "2. The Core Symptoms of ADHD", type: "Video", url: "https://youtu.be/ZLtHbKLRjkY" },
                    { id: 'v3', title: "3. How ADHD Affects Daily Life", type: "Video", url: "https://youtu.be/2ACKaVL4V4U" },
                    { id: 'v4', title: "4. The Neurology of ADHD", type: "Video", url: "https://youtu.be/XkGo62qdGL4" },
                    { id: 'v5', title: "5. Emotional Regulation and ADHD", type: "Video", url: "https://youtu.be/hxEUWgoVjOk" },
                    { id: 'v6', title: "6. The Experience of Rejection and Shame", type: "Video", url: "https://youtu.be/tfY-Z-Ijnes" },
                    { id: 'v7', title: "7. Coping Strategies and Daily Management", type: "Video", url: "https://youtu.be/AhiFl_6L698" },
                    { id: 'v8', title: "8. Professional Treatment and Support", type: "Video", url: "https://youtu.be/9Zazu9r3s9g" },
                    { id: 'v9', title: "9. The Strengths of the ADHD Mind", type: "Video", url: "https://youtu.be/i6P8ogCFTyA" },
                    { id: 'v10', title: "10. Living Well with ADHD", type: "Video", url: "https://youtu.be/aUqVkr8hmv4" },
                ] 
            }
        ] 
    },
    'eating-disorders': { 
        title: "Eating Disorders", 
        description: "Healthy relationships with food.", 
        modules: [
            { 
                id: 'core_101', 
                title: "Core Concepts (101 Series)", 
                resources: [
                    { id: 'v1', title: "1. Understanding Eating Disorders", type: "Video", url: "https://youtu.be/QvKMTjpLRdA" },
                    { id: 'v2', title: "2. The Different Types of Eating Disorders", type: "Video", url: "https://youtu.be/FJxRLQFPGo0" },
                    { id: 'v3', title: "3. How Eating Disorders Affect the Mind and Body", type: "Video", url: "https://youtu.be/yAccLIook34" },
                    { id: 'v4', title: "4. The Emotional Roots of Eating Disorders", type: "Video", url: "https://youtu.be/ywY0yOEs6Vo" },
                    { id: 'v5', title: "5. The Role of Society and Culture", type: "Video", url: "https://youtu.be/jSyhQ9KztwA" },
                    { id: 'v6', title: "6. Recognizing the Warning Signs", type: "Video", url: "https://youtu.be/Nr1YgqJBNFY" },
                    { id: 'v7', title: "7. The Recovery Process", type: "Video", url: "https://youtu.be/drKpXuR7ItA" },
                    { id: 'v8', title: "8. Treatment and Professional Support", type: "Video", url: "https://youtu.be/OweNOwSpaE0" },
                    { id: 'v9', title: "9. The Role of Support Systems", type: "Video", url: "https://youtu.be/PYvvgSCelvo" },
                    { id: 'v10', title: "10. Hope, Healing, and Resilience", type: "Video", url: "https://youtu.be/C3I8c9DGWl8" },
                ] 
            }
        ] 
    },
    addictions: { 
        title: "Addictions", 
        description: "Support systems.", 
        modules: [
             { 
                id: 'core_101', 
                title: "Core Concepts (101 Series)", 
                resources: [
                    { id: 'v1', title: "1. Understanding Substance Use and Recovery", type: "Video", url: "https://youtu.be/8-iZKKbSPfk" },
                    { id: 'v2', title: "2. Why People Turn to Substances", type: "Video", url: "https://youtu.be/4JMdXu2tX-g" },
                    { id: 'v3', title: "3. The Science of Addiction", type: "Video", url: "https://youtu.be/L-2Z_-ANavs" },
                    { id: 'v4', title: "4. Recognizing the Signs of Dependence", type: "Video", url: "https://youtu.be/cUToNT90gU8" },
                    { id: 'v5', title: "5. The Role of Stigma and Shame", type: "Video", url: "https://youtu.be/pOr1vkpCHp0" },
                    { id: 'v6', title: "6. The Journey Toward Recovery", type: "Video", url: "https://youtu.be/_2PwOUI59pM" },
                    { id: 'v7', title: "7. The Role of Therapy and Professional Support", type: "Video", url: "https://youtu.be/k5ZZnPPnIcI" },
                    { id: 'v8', title: "8. Building a Support Network", type: "Video", url: "https://youtu.be/mKuQF_yg5x8" },
                    { id: 'v9', title: "9. Preventing Relapse and Maintaining Growth", type: "Video", url: "https://youtu.be/NBbczbQYVkA" },
                    { id: 'v10', title: "10. Living a Life Beyond Recovery", type: "Video", url: "https://youtu.be/1phATR5VzJ8" },
                ] 
            }
        ] 
    },
    'ptsd-trauma': { 
        title: "PTSD & Trauma", 
        description: "Safe grounding tools.", 
        modules: [
            { 
                id: 'core_101', 
                title: "Core Concepts (101 Series)", 
                resources: [
                    { id: 'v1', title: "1. The Nature of Psychological Trauma", type: "Video", url: "https://youtu.be/8-iZKKbSPfk" },
                    { id: 'v2', title: "2. The Emotional Impact of Trauma", type: "Video", url: "https://youtu.be/4JMdXu2tX-g" },
                    { id: 'v3', title: "3. How Trauma Manifests in the Body", type: "Video", url: "https://youtu.be/L-2Z_-ANavs" },
                    { id: 'v4', title: "4. Cognitive and Behavioral Effects of Trauma", type: "Video", url: "https://youtu.be/cUToNT90gU8" },
                    { id: 'v5', title: "5. The Role of Memory and Triggers", type: "Video", url: "https://youtu.be/pOr1vkpCHp0" },
                    { id: 'v6', title: "6. The Long-Term Consequences of Unresolved Trauma", type: "Video", url: "https://youtu.be/_2PwOUI59pM" },
                    { id: 'v7', title: "7. The Healing Process", type: "Video", url: "https://youtu.be/k5ZZnPPnIcI" },
                    { id: 'v8', title: "8. The Importance of Support and Connection", type: "Video", url: "https://youtu.be/mKuQF_yg5x8" },
                    { id: 'v9', title: "9. Post-Traumatic Growth", type: "Video", url: "https://youtu.be/NBbczbQYVkA" },
                    { id: 'v10', title: "10. Moving Forward with Compassion", type: "Video", url: "https://youtu.be/1phATR5VzJ8" },
                ] 
            }
        ] 
    },
    gambling: { 
        title: "Gambling", 
        description: "Financial safety.", 
        modules: [
             { 
                id: 'core_101', 
                title: "Core Concepts (101 Series)", 
                resources: [
                    { id: 'v1', title: "1. Understanding Gambling Addiction", type: "Video", url: "https://youtu.be/hbwLtXeYlTo" },
                    { id: 'v2', title: "2. How Gambling Affects the Brain and Body", type: "Video", url: "https://youtu.be/r6t1cR0JWTQ" },
                    { id: 'v3', title: "3. Emotional Triggers and Vulnerabilities", type: "Video", url: "https://youtu.be/8Gva2AKF-0c" },
                    { id: 'v4', title: "4. The Cycle of Addiction", type: "Video", url: "https://youtu.be/FaYlbGshryw" },
                    { id: 'v5', title: "5. The Hidden Costs of Gambling", type: "Video", url: "https://youtu.be/4vZxl4W-TuE" },
                    { id: 'v6', title: "6. The Path to Recovery", type: "Video", url: "https://youtu.be/nnGpbPo743A" },
                    { id: 'v7', title: "7. The Importance of Support and Connection", type: "Video", url: "https://youtu.be/Mz2IgOui-TM" },
                    { id: 'v8', title: "8. Preventing Relapse and Building Resilience", type: "Video", url: "https://youtu.be/EMxPm39piu4" },
                    { id: 'v9', title: "9. Life Beyond Gambling", type: "Video", url: "https://youtu.be/8woIytTQXpQ" },
                ] 
            }
        ] 
    },
    
    // --- MENTAL PERFORMANCE SECTIONS (4 Base Videos Each) ---
    resilience: {
        title: "Resilience",
        description: "The capacity to recover quickly from difficulties.",
        modules: [
            { 
                id: 'fund_1', 
                title: "Performance Fundamentals", 
                resources: [
                    { id: 'p1', title: "1. Micro Dosing Adversity", type: "Video", url: "https://youtu.be/TMERth_0tvc" },
                    { id: 'p2', title: "2. The Battery: Resilience=Recovery", type: "Video", url: "https://youtu.be/cchSeBAWdI4" },
                    { id: 'p3', title: "3. The 3 P's: The Story You Tell", type: "Video", url: "https://youtu.be/gUu9We7JJyA" },
                    { id: 'p4', title: "4. The Shock Absorber: Redefining Resilience", type: "Video", url: "https://youtu.be/mM9xbjkZUJI" },
                ] 
            }
        ]
    },
    focus: { 
        title: "Focus", 
        description: "Eliminate distraction.", 
        modules: [
            { 
                id: 'fund_1', 
                title: "Performance Fundamentals", 
                resources: [
                    { id: 'p4', title: "4. The Spotlight: The Multitasking Myth", type: "Video", url: "https://youtu.be/rEFJVa2Nsoc" },
                    { id: 'p2', title: "2. The External Bouncer: Protect Your Focus", type: "Video", url: "https://youtu.be/wBKjlBGH4FU" },
                    { id: 'p1', title: "1. The Goldfish & The Submarine: Deep Work Matters", type: "Video", url: "https://youtu.be/4s-4k1RPvUg" },
                    { id: 'p3', title: "3. The Pomodoro Technique: Focus in Sprints", type: "Video", url: "https://youtu.be/nluqTJ9Sa0Y" },
                    
                ] 
            }
        ] 
    },
    confidence: { 
        title: "Confidence", 
        description: "Build self-belief.", 
        modules: [
            { 
                id: 'fund_1', 
                title: "Performance Fundamentals", 
                resources: [
                    { id: 'p1', title: "1. Hack Your Biology: Body Language", type: "Video", url: "https://youtu.be/mzx1PHD38s4" },
                    { id: 'p2', title: "2. The Inner Critic", type: "Video", url: "https://youtu.be/j0NcFtaz4Dg" },
                    { id: 'p3', title: "3. The Confidence Loop", type: "Video", url: "https://youtu.be/DZA6Pt53HBI" },
                    { id: 'p4', title: "4. The Confidence Myth", type: "Video", url: "https://youtu.be/XJqpm20WoHg" },
                ] 
            }
        ] 
    },
    motivation: { 
        title: "Motivation", 
        description: "Moving from wanting to doing.", 
        modules: [
            { 
                id: 'fund_1', 
                title: "Performance Fundamentals", 
                resources: [
                    { id: 'p1', title: "1. The Motivation Trap Action First", type: "Video", url: "https://youtu.be/V3UhNt_fZp4" },
                    { id: 'p2', title: "2. The Spark vs The Fire Intrinsic vs Extrinsic", type: "Video", url: "https://youtu.be/RuDuZFiRfNI" },
                    { id: 'p3', title: "3. Staying in the Tightrope Zone", type: "Video", url: "https://youtu.be/tc-ijT_2zxU" },
                    { id: 'p4', title: "4. The 5 Whys: Finding Your Real Motivation", type: "Video", url: "https://youtu.be/ggxmGQIpjO4" },
                ] 
            }
        ] 
    },
    'self-awareness': { 
        title: "Self-Awareness", 
        description: "Understanding internal state.", 
        modules: [
            { 
                id: 'fund_1', 
                title: "Performance Fundamentals", 
                resources: [
                    { id: 'p1', title: "1. The Lens", type: "Video", url: "https://youtu.be/N37lHADCsjY" },
                    { id: 'p2', title: "2. The Gap", type: "Video", url: "https://youtu.be/imKRFa-K3wo" },
                    { id: 'p3', title: "3. Name It to Tame It", type: "Video", url: "https://youtu.be/lAinQcgESLI" },
                    { id: 'p4', title: "4. The Dance Floor vs The Balcony", type: "Video", url: "https://youtu.be/VMcPEJaQ3Hs" },
                ] 
            }
        ] 
    },
    'self-discipline': { 
        title: "Self-Discipline", 
        description: "Consistency over intensity.", 
        modules: [
            { 
                id: 'fund_1', 
                title: "Performance Fundamentals", 
                resources: [
                    { id: 'p1', title: "1. The 'Just Five Minutes' Rule", type: "Video", url: "https://youtu.be/9YaYMCq0os8" },
                    { id: 'p2', title: "2. The Identity Shift", type: "Video", url: "https://youtu.be/QBVoM3sQG4E" },
                    { id: 'p3', title: "3. The Two Yous", type: "Video", url: "https://youtu.be/xQgv8Izd7a4" },
                    { id: 'p4', title: "4. The Decision Fatigue", type: "Video", url: "https://youtu.be/pi-kav89Qxc" },
                ] 
            }
        ] 
    },
    adaptability: { 
        title: "Adaptability", 
        description: "Thriving in change.", 
        modules: [
            { 
                id: 'fund_1', 
                title: "Performance Fundamentals", 
                resources: [
                    { id: 'p1', title: "1. The 'Yes, And' Mindset", type: "Video", url: "https://youtu.be/_JsmVragu1A" },
                    { id: 'p2', title: "2. The Learn Unlearn Cycle", type: "Video", url: "https://youtu.be/mJL_SuijZ-Q" },
                    { id: 'p3', title: "3. The Map vs  The Territory", type: "Video", url: "https://youtu.be/nonxH6ZFMGo" },
                    { id: 'p4', title: "4. Bamboo vs The Oak", type: "Video", url: "https://youtu.be/T9d3jDjXHO0" },
                ] 
            }
        ] 
    },
    'nutrition-hydration': { 
        title: "Nutrition & Hydration", 
        description: "Fueling the engine.", 
        modules: [
            { 
                id: 'fund_1', 
                title: "Performance Fundamentals", 
                resources: [
                    { id: 'p1', title: "1. The Fire & The Logs", type: "Video", url: "https://youtu.be/VolD2TPQMeM" },
                    { id: 'p2', title: "2. The 'Good Enough' Strategy", type: "Video", url: "https://youtu.be/66xiFMubTeI" },
                    { id: 'p3', title: "3. Why It Breaks Down", type: "Video", url: "https://youtu.be/ZwxSLFG5qgU" },
                    { id: 'p4', title: "4. Not Just Fuel, But Information", type: "Video", url: "https://youtu.be/bN-FlfmdS-w" },
                ] 
            }
        ] 
    },
    'pain-injury': { 
        title: "Pain & Injury", 
        description: "Recovery psychology.", 
        modules: [
            { 
                id: 'fund_1', 
                title: "Performance Fundamentals", 
                resources: [
                    { id: 'p1', title: "1. The Alarm System", type: "Video", url: "https://youtu.be/gPel0wQWZYg" },
                    { id: 'p2', title: "2. The Traffic Light System", type: "Video", url: "https://youtu.be/QvgmYKsyMpw" },
                    { id: 'p3', title: "3. Motion is Lotion", type: "Video", url: "https://youtu.be/YK-6v2YOVNs" },
                    { id: 'p4', title: "4. The Sensitive Alarm", type: "Video", url: "https://youtu.be/5YiPLlO6pIM" },
                ] 
            }
        ] 
    },
    
    // --- OTHER SECTIONS ---
    sleep: {
        title: "Sleep",
        description: "Mastering the foundation of physical and cognitive recovery.",
        modules: [
            { id: 'm1', title: "1. Sleep 101", resources: [{ id: 's3', title: "Circadian Rhythm Protocol", type: "Guide", url: "" }, { id: 's4', title: "Evening Wind-Down", type: "Checklist", url: "" }] },
            { id: 'm2', title: "2. Sleep 102", resources: [{ id: 's1', title: "Sleep Hygiene Scorecard", type: "Worksheet", url: "" }, { id: 's2', title: "NSDR (Deep Rest)", type: "Video", url: "https://www.youtube.com/watch?v=pL02HRFk2vo" }] },
            { id: 'm3', title: "3. Sleep 103", resources: [{ id: 's5', title: "Advanced Sleep Tracking", type: "Guide", url: "" }, { id: 's6', title: "Travel & Jet Lag Protocols", type: "PDF", url: "" }] }
        ]
    },
    'career': {
        title: "Career Growth",
        description: "Navigating professional transitions.",
        // TODO: restore these modules when career content is authored. Their resources are
        // empty today, so we ship modules: [] — TopicView then renders its clean
        // "No content has been added to this topic yet." empty state instead of
        // expandable modules that dead-end in "This module is empty."
        // modules: [
        //     { id: 'm1', title: "Leadership Skills", resources: [] },
        //     { id: 'm2', title: "Career Pathing", resources: [] }
        // ]
        modules: []
    }
};

export const RECOVERY_METRICS = [
    { label: "Stress Load", value: "Moderate", icon: <Activity size={24}/>, color: "text-green-500", bg: "bg-green-50" },
    { label: "Sleep Quality", value: "Moderate", icon: <Moon size={24}/>, color: "text-orange-500", bg: "bg-orange-50" },
    { label: "Mental Focus", value: "Optimal", icon: <Brain size={24}/>, color: "text-green-500", bg: "bg-green-50" }
];

export const FAMILY_HUDDLE_VIDEOS = {
    1: 'x5-NzkPRXYY', 2: 'v6RNJlMTFGA', 3: 'vrVV7_xGydI', 4: 'h1hArsapIx4', 
    5: 'vGZgh9Zhhis', 6: 'Grbf7YZIZc4', 7: 'oR2CqoxKZZc', 8: '0Z9Aa0diXjM'
};

export const DAILY_QUOTES = [
    { text: "Rest is not idleness, it is the fuel for tomorrow's performance.", author: "Rest & Recovery" },
    { text: "Focus on the step in front of you, not the whole staircase.", author: "Mental Focus" },
    { text: "Consistency is what happens when you commit to the process, not the outcome.", author: "Discipline" },
    { text: "Your battery needs charging just as much as your phone does.", author: "Wellness" },
    { text: "Pressure is a privilege. It means you're in the game.", author: "Billie Jean King" },
    { text: "You don't rise to the level of your goals, you fall to the level of your systems.", author: "James Clear" },
    { text: "A calm mind identifies solutions. A chaotic mind finds problems.", author: "Emotional Control" },
    { text: "Sleep is the single most effective performance enhancing tool we have.", author: "Matt Walker" },
    { text: "Tough times don't last, but tough people do.", author: "Resilience" },
    { text: "Don't count the days, make the days count.", author: "Muhammad Ali" },
    { text: "Comparison is the thief of joy. Run your own race.", author: "Mental Health" },
    { text: "The only workout you regret is the one you didn't do.", author: "Physical Activation" },
    { text: "Vulnerability sounds like truth and feels like courage.", author: "Brené Brown" },
    { text: "Win the morning, win the day.", author: "Routine" },
    { text: "Excellence is not an act, but a habit.", author: "Aristotle" },
    { text: "Self-care is how you take your power back.", author: "Lalah Delia" },
    { text: "It's okay to not be okay, but it's not okay to stay that way.", author: "Action" },
    { text: "Small daily improvements are the key to staggering long-term results.", author: "Growth Mindset" },
    { text: "Hydration is the cheapest performance hack in the world.", author: "Nutrition" },
    { text: "Listen to your body when it whispers so you don't have to hear it scream.", author: "Injury Prevention" },
    { text: "Failure is simply the opportunity to begin again, this time more intelligently.", author: "Henry Ford" },
    { text: "Be where your feet are.", author: "Mindfulness" },
    { text: "Discipline is choosing what you want most over what you want now.", author: "Self-Control" },
    { text: "Trust the timing of your life.", author: "Patience" },
    { text: "Movement is medicine.", author: "Physical Health" },
    { text: "Setbacks are setups for comebacks.", author: "Resilience" },
    { text: "Your energy introduces you before you even speak.", author: "Presence" },
    { text: "Deep work requires deep rest.", author: "Cognitive Performance" },
    { text: "Comfort is the enemy of progress.", author: "P.T. Barnum" },
    { text: "The best project you'll ever work on is you.", author: "Self-Development" },
    { text: "Breathe. It’s just a bad day, not a bad life.", author: "Perspective" }
];

export const SQUAD_PLAYBOOKS = {
    'sleep_perf': {
        title: "Sleep Optimization",
        description: "Quality sleep is the ultimate performance enhancer. Focus on these 3 habits to improve recovery.",
        actions: [
            { title: "The 3-2-1 Rule", text: "Stop eating 3 hours before bed. Stop working 2 hours before. Stop screens 1 hour before." },
            { title: "Temperature Drop", text: "Keep the bedroom between 15-19°C (60-67°F) to signal the brain to rest." },
            { title: "Consistent Wake Time", text: "Wake up at the exact same time every day, even on weekends, to anchor your circadian rhythm." }
        ],
        coachPlan: [
            { phase: "Pre-Practice", duration: "5 Mins", title: "The 1% Rule", desc: "Explain that a 1% drop in hydration or sleep leads to a 10% drop in reaction time. Ask who got 8 hours last night." },
            { phase: "During Practice", duration: "Ongoing", title: "Fatigue Identification", desc: "Watch for late reactions or sloppy mechanics in the final 20 minutes. Stop the drill and explicitly tie the mental errors to physical fatigue." },
            { phase: "Post-Practice", duration: "3 Mins", title: "Wind-Down Commitment", desc: "Have every team member state their target bedtime and screen-off time for tonight before breaking the huddle." }
        ]
    },
    'focus': {
        title: "Building Deep Focus",
        description: "Attention is a muscle. Train it by eliminating distractions and working in sprints.",
        actions: [
            { title: "The Phone Bouncer", text: "Keep your phone in another room or out of sight during deep work or practice sessions." },
            { title: "The 20-Minute Sprint", text: "Pick one single task. Set a timer for 20 minutes. Do nothing else until it rings." },
            { title: "Spotlight Thinking", text: "When distracted, literally say 'Spotlight' out loud to physically redirect your attention back to the present." }
        ],
        coachPlan: [
            { phase: "Pre-Practice", duration: "5 Mins", title: "The Spotlight Concept", desc: "Introduce the idea of 'Spotlight' attention. Ask athletes to identify 2 external distractions they will actively ignore today." },
            { phase: "During Practice", duration: "10 Mins", title: "Distraction Drill", desc: "Run a high-intensity drill but intentionally introduce bad calls, loud music, or external noise. Stop the drill if focus breaks, reset with a breath, and go again." },
            { phase: "Post-Practice", duration: "3 Mins", title: "Focus Audit", desc: "Ask 3 athletes to share a moment where they lost focus today and how quickly they were able to bring the 'Spotlight' back." }
        ]
    },
    'resilience': {
        title: "Bouncing Back (Resilience)",
        description: "Resilience isn't about avoiding failure; it's about reducing the time it takes to recover from it.",
        actions: [
            { title: "The 24-Hour Rule", text: "Give yourself exactly 24 hours to celebrate a massive win or sulk over a tough loss. Then, move on." },
            { title: "Control the Controllables", text: "Draw a circle. Put what you control inside (effort, attitude). Put what you don't outside (refs, weather). Ignore the outside." },
            { title: "The 'Good' Reframing", text: "When a setback happens, train yourself to immediately say 'Good. Now I have a chance to practice overcoming this.'" }
        ],
        coachPlan: [
            { phase: "Pre-Practice", duration: "5 Mins", title: "Flush The Mistake", desc: "Introduce a physical 'flush' mechanism (e.g., wiping dirt off your shoulder, tapping a helmet) to signify moving on from an error." },
            { phase: "During Practice", duration: "15 Mins", title: "Unfair Advantage Drill", desc: "Stack the teams unfairly or restrict a key player. Award double points for how quickly the disadvantaged team recovers from a sudden momentum shift." },
            { phase: "Post-Practice", duration: "5 Mins", title: "Failure High-Five", desc: "Have players explicitly shout out a teammate who failed during practice but immediately sprinted back into the play." }
        ]
    },
    'motivation': {
        title: "Sustaining Motivation",
        description: "Motivation is a feeling, but drive is a choice. Moving from wanting to doing.",
        actions: [
            { title: "Remember the Why", text: "Write down the core reason you are doing this. Look at it before your hardest sessions." },
            { title: "Micro-Wins", text: "Set absurdly small goals (e.g., 'Just put on my running shoes'). Momentum builds from small victories." },
            { title: "Action Precedes Feeling", text: "Don't wait to feel motivated to start. Start moving, and the motivation will follow." }
        ],
        coachPlan: [
            { phase: "Pre-Practice", duration: "5 Mins", title: "The 'Why' Wall", desc: "Have every athlete write one personal reason they are committed to the team on a whiteboard before stepping on the field/floor." },
            { phase: "During Practice", duration: "Ongoing", title: "Micro-Targeting", desc: "Stop focusing on the final score of the scrimmage. Set micro-targets (e.g., 'First team to 3 consecutive perfect passes wins')." },
            { phase: "Post-Practice", duration: "3 Mins", title: "Effort Praise", desc: "Publicly praise effort over execution. Highlight the player who worked the hardest, regardless of their statistical output today." }
        ]
    },
    'confidence': {
        title: "Building Confidence",
        description: "Confidence is earned through evidence. Build your bank of self-belief.",
        actions: [
            { title: "The Evidence Bank", text: "Write down 3 difficult things you have successfully overcome in the past. Read it when in doubt." },
            { title: "Body Language Hack", text: "Stand tall, chin up, shoulders back. Your physiology dictates your psychology." },
            { title: "Self-Talk Audit", text: "Talk to yourself the exact same way you would talk to a teammate who is struggling." }
        ],
        coachPlan: [
            { phase: "Pre-Practice", duration: "3 Mins", title: "Posture Check", desc: "Enforce 'Level 10' body language during the huddle. No slouching, eye contact required. Physiology drives psychology." },
            { phase: "During Practice", duration: "10 Mins", title: "Success Logging", desc: "Run a drill where athletes are ONLY allowed to communicate positive reinforcement. Stop the play immediately if negative self-talk is vocalized." },
            { phase: "Post-Practice", duration: "5 Mins", title: "Evidence Collection", desc: "Go around the circle and have each athlete state one thing they did better today than they did yesterday." }
        ]
    },
    'discipline': {
        title: "Self-Discipline",
        description: "Consistency over intensity. Doing the work even when you don't feel like it.",
        actions: [
            { title: "The 5-Minute Rule", text: "Commit to doing the hard task for just 5 minutes. Usually, that's enough to break the friction." },
            { title: "Environment Design", text: "Make good habits easy (leave gym clothes out) and bad habits hard (unplug the TV)." },
            { title: "Process Over Outcome", text: "Focus entirely on executing the next rep correctly, rather than obsessing over the final score." }
        ],
        coachPlan: [
            { phase: "Pre-Practice", duration: "5 Mins", title: "Standard Setting", desc: "Define the 'Standard of the Day' (e.g., 'Nobody walks back to the huddle'). Discipline is about holding the line." },
            { phase: "During Practice", duration: "Ongoing", title: "The Little Things", desc: "Do not let small details slide. If a drill requires touching the line, and they miss it by an inch, reset the entire drill." },
            { phase: "Post-Practice", duration: "2 Mins", title: "Locker Room Check", desc: "Discipline doesn't end at the whistle. Ensure the space is left cleaner than when you arrived." }
        ]
    },
    'awareness': {
        title: "Self-Awareness",
        description: "Understanding your internal state so you can regulate it.",
        actions: [
            { title: "Name It to Tame It", text: "When feeling overwhelmed, pause and explicitly name the emotion ('I am feeling anxious')." },
            { title: "The Balcony View", text: "Imagine stepping out of your body and watching yourself from a balcony. Observe objectively." },
            { title: "Daily Check-in", text: "Ask yourself every morning: 'What is my energy level today on a scale of 1-10?' Adjust accordingly." }
        ],
        coachPlan: [
            { phase: "Pre-Practice", duration: "3 Mins", title: "Fist to Five", desc: "Have athletes hold up a hand (1-5 fingers) indicating their physical/mental battery level today before starting." },
            { phase: "During Practice", duration: "Spot Check", title: "Freeze Frame", desc: "Blow the whistle randomly and ask a player, 'What are you feeling right now?' Train them to articulate their internal state under pressure." },
            { phase: "Post-Practice", duration: "5 Mins", title: "The Balcony Debrief", desc: "Ask the team to assess their own practice from 'The Balcony'. 'If a scout was watching us today, what would they write down?'" }
        ]
    },
    'adaptability': {
        title: "Adaptability",
        description: "Thriving in change. The ability to pivot without panic.",
        actions: [
            { title: "The 'Yes, And' Rule", text: "Accept the new reality immediately ('Yes, this happened') and focus on the next step ('And here is what I'll do')." },
            { title: "Scenario Planning", text: "Before a big event, visualize what you will do if Plan A entirely fails." },
            { title: "Breath Reset", text: "When surprised by a sudden change, take 3 deep box breaths to reset your nervous system before reacting." }
        ],
        coachPlan: [
            { phase: "Pre-Practice", duration: "2 Mins", title: "Chaos Warning", desc: "Tell the team: 'I am going to intentionally change the rules mid-drill today. I expect zero complaining and immediate adaptation.'" },
            { phase: "During Practice", duration: "15 Mins", title: "Rule Swap Drill", desc: "Change the parameters of the scrimmage every 3 minutes (e.g., no dribbling, silent communication only, point values flip). Reward the fastest pivot." },
            { phase: "Post-Practice", duration: "3 Mins", title: "Friction Debrief", desc: "Discuss who handled the rule changes best and why. Emphasize that frustration is the enemy of adaptability." }
        ]
    },
    'nutrition': {
        title: "Nutrition & Hydration",
        description: "Your body is an engine. The fuel you put in directly impacts the output.",
        actions: [
            { title: "Hydration Baseline", text: "Drink a large glass of water immediately upon waking before consuming coffee or food." },
            { title: "Eat the Rainbow", text: "Aim to have at least 3 different colors of whole foods on your plate at dinner." },
            { title: "Protein Anchor", text: "Ensure every meal contains a solid protein source to stabilize blood sugar and focus." }
        ],
        coachPlan: [
            { phase: "Pre-Practice", duration: "Check", title: "Urine Test Talk", desc: "Remind athletes that if their urine isn't clear or pale yellow, they are already operating at a physical and cognitive deficit." },
            { phase: "During Practice", duration: "Breaks", title: "Mandatory Hydration", desc: "Do not let water breaks be optional. Build specific, structured 60-second fueling windows into the practice plan." },
            { phase: "Post-Practice", duration: "2 Mins", title: "The 30-Minute Window", desc: "Remind the team of the glycogen recovery window. They need protein and carbs within 30-45 minutes of leaving the facility." }
        ]
    },
    'injury_mgmt': {
        title: "Pain & Injury Management",
        description: "Navigating physical setbacks with intelligence rather than ego.",
        actions: [
            { title: "Listen to the Whisper", text: "Address small tweaks and tightness before they escalate into full-blown screams." },
            { title: "Active Recovery", text: "Instead of total rest, use light movement like walking or stretching to promote blood flow." },
            { title: "The Traffic Light", text: "Green = Go hard. Yellow = Modify the movement. Red = Stop. Never push through sharp, shooting pain." }
        ],
        coachPlan: [
            { phase: "Pre-Practice", duration: "5 Mins", title: "Honesty Policy", desc: "Create a safe environment for reporting pain. Differentiate between 'soreness' (push through) and 'sharp pain' (stop immediately)." },
            { phase: "During Practice", duration: "Ongoing", title: "Modify, Don't Sit", desc: "If a player has an upper-body injury, they are on a bike or doing core work. Keep injured players actively engaged in the team environment." },
            { phase: "Post-Practice", duration: "3 Mins", title: "Treatment Accountability", desc: "Assign an accountability partner. If an athlete needs ice or treatment, their partner ensures they go to the training room before leaving." }
        ]
    }
};